// This modal is for adding district 3 districts for a specific type of district, and specific tier 2 parent.
// It is used in the Tier2DistrictDetails screen.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { District, DistrictSource } from '../../app/feathers/districts/District';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import {
  Modal,
  RadioGroup,
  Radio,
  FormControlLabel,
  Typography,
  Button,
  TextField,
  Checkbox,
  IconButton,
  Chip,
  Skeleton,
  Tooltip
} from '@mui/material';
import { ModalInner } from '../lower-order';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Cancel, KeyboardReturn, MapOutlined } from '@mui/icons-material';
import { isPermitted, researcherPermissionsIncludingAndAfter, useFeathers } from '../../app/util';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { DistrictVotingType } from '../../app/feathers/districts/DistrictVotingType';
import { NamingPattern } from '../../app/feathers/naming-patterns/NamingPattern';
import { prettyDistrictNameFromPattern } from '../../app/util/pretty-district-name-from-pattern';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/redux/root-state';
import moment from 'moment';
import {
  DistrictIdentifierType,
  identifierConversionSummary,
  identifierPayload,
  identifierTypeForDistrict,
  identifierValueForDistrict,
  initialIdentifierTypeForDistricts,
  validateDistrictIdentifiers,
} from './district-identifiers';

interface CreateOperation {
  operationType: 'create';
  id?: never;
  rowIndex: number;
  value: Partial<District>;
}

interface PatchOperation {
  operationType: 'patch';
  id: string;
  rowIndex: number;
  value: Partial<District>;
}

interface DeleteOperation {
  operationType: 'delete';
  id: string;
  value?: never;
}

interface Child {
  identifier: District['name' | 'number' | 'letter'];
  votingType: DistrictVotingType;
  existingDistrictId?: string;
}

interface Tier3AddDistrictModalProps {
  parentDistrict: District | null;
  tier3Type: string;
  open: boolean;
  existingSubdistricts: District[];
  stateConfiguration: StateConfiguration | undefined;
  onClose: () => void;
  onFinish: (parentDistrict: District, subdistricts: District[], hasFailures?: boolean) => void;
  note?: string;
  sources?: DistrictSource[];
}

const Tier3AddDistrictModal = (
  {
    parentDistrict,
    existingSubdistricts,
    stateConfiguration,
    tier3Type,
    onClose,
    onFinish,
    open,
  }: Tier3AddDistrictModalProps,
) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const user = useSelector((state: RootState) => state.user);

  const [districtGroupOption, setDistrictGroupOption] = useState<'has-districts' | 'no-districts' | null>(null);
  const [children, setChildren] = useState<Child[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [note, setNote] = useState<string>('');
  const [sourcesGiven, setSourcesGiven] = useState<string[]>([]);
  const [noteGiven, setNoteGiven] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [orderedNamingPatterns, setOrderedNamingPatterns] = useState<(NamingPattern | null)[]>([]);
  const [pendingGeodistrictRemovals, setPendingGeodistrictRemovals] = useState<Set<string>>(new Set());
  const [identifierType, setIdentifierType] = useState<DistrictIdentifierType>('name');
  const [allDistrictsFound, setAllDistrictsFound] = useState(true);

  const loadNamingPatterns = async () => {
    if(!parentDistrict) return;
    const stateDistrict = await feathers.getService('districts').get(parentDistrict._id);
    const namingPatternService = feathers.getService('naming-patterns');

    const stateNamingPatternId = stateDistrict?.namingPatternId;

    const namingPatterns = (await Promise.all([
      namingPatternService.find({ query: { key: 'default-national'} }),
      namingPatternService.find({ query: { _id: stateNamingPatternId }}),
      namingPatternService.find({ query: { _id: parentDistrict.namingPatternId }}),
    ])).map((result) => result.data?.[0]);
    
    // filter out duplicates, keep empty values instead of filtering out
    const patternsDeduped = namingPatterns.map((pattern, index, arr) => {
      const arrBefore = arr.slice(0, index);
      return arrBefore.find(p => p?.key === pattern?.key) ? null : pattern;
    })

    setOrderedNamingPatterns(patternsDeduped.reverse());
  }

  const humanizedTier3Type = useMemo(() => humanizeDistrictType(tier3Type), [tier3Type]);

  const possibleChildTypesForParent = useMemo(() => {
    if (!parentDistrict || !stateConfiguration) {
      return [];
    }

    return stateConfiguration.typeStructure[parentDistrict.type] || [];
  }, [parentDistrict, stateConfiguration]);

  const identifierValidation = useMemo(
    () => validateDistrictIdentifiers(children, identifierType),
    [children, identifierType],
  );

  const conversionSummary = useMemo(
    () => identifierConversionSummary(existingSubdistricts, identifierType),
    [existingSubdistricts, identifierType],
  );

  const operationsToPerform = useMemo(() => {
    // this will return an object with two keys: childrenStructureValue and districts
    // childrenStructureValue: the value to patch the parent district with (the final count of districts)
    // districts an array of operations that will be performed
    // each with the format of :
    // { operationType ('create', 'delete', 'patch'), id (only for delete and patch), value (the body )}

    if (districtGroupOption === 'has-districts') {
      // if we have districts, we need to compare the existing districts to the new districts
      if (existingSubdistricts.length === 0) {
        // this is our simplest case, just creating new districts
        const districts = children.map<CreateOperation>((d, index) => {
            const value: Partial<District> = {
              type: tier3Type,
              parentId: parentDistrict?._id,
              votingType: d.votingType,
              ...identifierPayload(identifierType, identifierValidation.normalizedValues[index]),
            };
            return { operationType: 'create', rowIndex: index, value };
          });
        return {
          childrenStructureValue: districts.length,
          districts,
        };
      } else {
        // this means that there are already districts, and we are performing some sort of edit on those districts
        // we need to compare the existing districts to the new districts
        const newDistricts = children;
        const districtsBeingCreated = newDistricts.filter(d => !d.existingDistrictId);
        const districtsBeingDeleted = existingSubdistricts.filter(ed => !newDistricts.find(d => d.existingDistrictId === ed._id));
        const districtsBeingEdited = newDistricts.filter((d, index) => {
          if (!d.existingDistrictId) {
            return false;
          }
          const existingDistrict = existingSubdistricts.find(ed => ed._id === d.existingDistrictId);
          if (!existingDistrict) {
            return false;
          }
          const existingType = identifierTypeForDistrict(existingDistrict);
          return (
            existingType !== identifierType ||
            `${existingDistrict[identifierType] ?? ''}` !== `${identifierValidation.normalizedValues[index] ?? ''}` ||
            existingDistrict.votingType !== d.votingType
          );
        });

        let districts = [
          ...districtsBeingCreated.map<CreateOperation>(d => {
            const childIndex = children.indexOf(d);
            const value: Partial<District> = {
              type: tier3Type,
              parentId: parentDistrict?._id,
              votingType: d.votingType,
              ...identifierPayload(identifierType, identifierValidation.normalizedValues[childIndex]),
            };
            return { operationType: 'create', rowIndex: childIndex, value };
          }),
          ...districtsBeingDeleted.map<DeleteOperation>(d => ({ operationType: 'delete', id: d._id })),
          ...districtsBeingEdited.map(d => {
            const childIndex = children.indexOf(d);
            const value: Partial<District> = {
              votingType: d.votingType,
              ...identifierPayload(identifierType, identifierValidation.normalizedValues[childIndex]),
            };
            return { operationType: 'patch', id: d.existingDistrictId, rowIndex: childIndex, value };
          }).filter((o): o is PatchOperation => !!o.id),
        ];

        return {
          childrenStructureValue: newDistricts.length,
          districts,
        };
      }
    } else if (districtGroupOption === 'no-districts') {
      // if we are marking this district as having no districts
      const districts = existingSubdistricts.map<DeleteOperation>(d => ({ operationType: 'delete', id: d._id }));
      return {
        childrenStructureValue: 0,
        districts,
      };
    } else {
      return {
        childrenStructureValue: -1,
        districts: [],
      };
    }
  }, [districtGroupOption, children, existingSubdistricts, identifierType, identifierValidation.normalizedValues, parentDistrict, tier3Type]);

  const geodistrictIdsToDelete = useMemo(() => {
    const districtIdsBeingDeleted = new Set(
      operationsToPerform.districts
        .filter((operation): operation is DeleteOperation => operation.operationType === 'delete')
        .map(operation => operation.id)
    );

    return [...pendingGeodistrictRemovals].filter(districtId => !districtIdsBeingDeleted.has(districtId));
  }, [operationsToPerform, pendingGeodistrictRemovals]);

  const [operationReady, operationSummary] = useMemo(() => {
    const sourcesFiltered = sources.filter(Boolean);
    const sourcesMissing = sourcesFiltered.length === 0;

    // This will summarize the operation that takes place, if someone clicks save
    if (districtGroupOption === 'has-districts') {
      const hasUpsertOperations = operationsToPerform.districts.some(
        district => district.operationType === 'create' || district.operationType === 'patch',
      );
      if (hasUpsertOperations && !identifierValidation.isValid) {
        return [false, ''];
      }
      if (operationsToPerform.districts.length > 0) {
        const districtsBeingCreated = operationsToPerform.districts.filter(d => d.operationType === 'create').length;
        const districtsBeingDeleted = operationsToPerform.districts.filter(d => d.operationType === 'delete').length;
        const districtsBeingEdited = operationsToPerform.districts.filter(d => d.operationType === 'patch').length;
        if (districtsBeingCreated > 0 && sourcesMissing) {
          return [false, ''];
        }

        const summary = [
          districtsBeingCreated > 0 ? `Creating ${districtsBeingCreated} districts (with identifier ${identifierType}).` : false,
          districtsBeingDeleted > 0 ? `Deleting ${districtsBeingDeleted} districts.` : false,
          districtsBeingEdited > 0 ? `Updating ${districtsBeingEdited} districts.` : false,
          geodistrictIdsToDelete.length > 0 ? `Deleting ${geodistrictIdsToDelete.length} geodistricts.` : false,
          conversionSummary || false,
        ].filter(Boolean).join(' ');

        return [
          true,
          summary,
        ];
      }
    } else if (districtGroupOption === 'no-districts') {
      if (operationsToPerform.districts.length > 0) {
        const districtsBeingDeleted = operationsToPerform.districts.filter(d => d.operationType === 'delete').length;
        return [
          true,
          `This will delete ${districtsBeingDeleted} subdistricts and mark the parent district as having no ${humanizedTier3Type} subdistricts.`,
        ];
      } else if (parentDistrict?.childrenStructure && parentDistrict.childrenStructure[tier3Type] !== operationsToPerform.childrenStructureValue) {
        return [true, `This will set this parent district as having no ${humanizedTier3Type} subdistricts.`];
      }
    } 

    const sourcesGivenMapped = sourcesGiven.filter(Boolean).map(source => source.trim()).join(', ');
    const sourcesFilteredMapped = sourcesFiltered.join(', ');
    
    const noteUpdated = note !== noteGiven;
    const sourcesUpdated = sourcesFilteredMapped !== sourcesGivenMapped;
    if (noteUpdated || sourcesUpdated) {
      const metadataSummary = `${[noteUpdated ? 'Notes' : undefined, sourcesUpdated ? 'Sources' : undefined].filter(Boolean).join(' and ')} will be updated.`;
      const geodistrictSummary = geodistrictIdsToDelete.length > 0
        ? ` Deleting ${geodistrictIdsToDelete.length} geodistricts.`
        : ' This will not change the districts.';
      return [true, `${metadataSummary}${geodistrictSummary}`];
    }

    if (geodistrictIdsToDelete.length > 0) {
      return [true, `Deleting ${geodistrictIdsToDelete.length} geodistricts. District records will be kept.`];
    }

    return [false, ''];
  }, [operationsToPerform, identifierType, identifierValidation.isValid, conversionSummary, districtGroupOption, sources, note, noteGiven, sourcesGiven, geodistrictIdsToDelete, humanizedTier3Type, parentDistrict, tier3Type]);

  const operationPending = useMemo(() => {
    const sourcesGivenMapped = sourcesGiven.filter(Boolean).map(source => source.trim()).join(', ');
    const sourcesFilteredMapped = sources.filter(Boolean).map(source => source.trim()).join(', ');
    const childrenStructureUpdated = districtGroupOption === 'no-districts'
      && parentDistrict?.childrenStructure
      && parentDistrict.childrenStructure[tier3Type] !== operationsToPerform.childrenStructureValue;

    return (
      operationsToPerform.districts.length > 0
      || Boolean(childrenStructureUpdated)
      || note !== noteGiven
      || sourcesFilteredMapped !== sourcesGivenMapped
      || geodistrictIdsToDelete.length > 0
    );
  }, [
    districtGroupOption,
    geodistrictIdsToDelete,
    note,
    noteGiven,
    operationsToPerform,
    parentDistrict,
    sources,
    sourcesGiven,
    tier3Type,
  ]);

  const operationBlockedReason = useMemo(() => {
    if (!operationPending || operationReady) return '';

    const hasUpsertOperations = operationsToPerform.districts.some(
      district => district.operationType === 'create' || district.operationType === 'patch',
    );
    if (hasUpsertOperations && !identifierValidation.isValid) {
      return 'Fix the district identifier errors to enable Confirm.';
    }
    const hasCreateOperations = operationsToPerform.districts.some(
      district => district.operationType === 'create',
    );
    if (hasCreateOperations && !sources.some(source => source.trim())) {
      return 'Add at least one source to enable Confirm.';
    }
    return 'Complete the required fields to enable Confirm.';
  }, [identifierValidation.isValid, operationPending, operationReady, operationsToPerform, sources]);

  const textFieldRefs = useRef<HTMLInputElement[]>([]);
  const [focusRowIndex, setFocusRowIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open && feathers) {
      loadNamingPatterns();
    }
  }, [open, feathers]);

  useEffect(() => {
    if (!open) {
      setDistrictGroupOption(null);
      setChildren([]);
      setSaving(false);
      setError(null);
      setSources([]);
      setNote('');
      setSourcesGiven([]);
      setNoteGiven('');
      setPendingGeodistrictRemovals(new Set());
      setIdentifierType('name');
      setAllDistrictsFound(true);
    } else {
      setIdentifierType(initialIdentifierTypeForDistricts(existingSubdistricts));
      // when we first open, set the state values based on if we already have some info
      const childrenStructureForParent = parentDistrict?.childrenStructure;
      if (childrenStructureForParent && typeof (childrenStructureForParent[tier3Type]) !== 'undefined') {
        const numChildren = childrenStructureForParent[tier3Type];
        if (numChildren === 0) {
          setDistrictGroupOption('no-districts');
        } else if (numChildren > 0 || existingSubdistricts.length > 0) {
          setDistrictGroupOption('has-districts');
          const newChildren = existingSubdistricts.map(d => ({
            identifier: identifierValueForDistrict(d),
            votingType: d.votingType,
            existingDistrictId: d._id,
          }));
          setChildren(newChildren);

          // If there is a mismatch between the children structure and actual children, display an error message
          if (numChildren !== existingSubdistricts.length) {
            setError('The number of districts in the children structure does not match the actual number of districts.');
          }
        }
      } else if (existingSubdistricts.length > 0) {
        setDistrictGroupOption('has-districts');
        const newChildren = existingSubdistricts.map(d => ({
          identifier: identifierValueForDistrict(d),
          votingType: d.votingType,
          existingDistrictId: d._id,
        }));
        setChildren(newChildren);
        setError('The children structure for this district is missing. Please update the children structure to match the actual number of districts.');
      }

      if (parentDistrict?.sources) {
        const sourcesFiltered = parentDistrict.sources.filter(source => source.type === tier3Type).map(source => source.url);
        setSources([
          ...sourcesFiltered,
          ''
        ]);
        setSourcesGiven(sourcesFiltered);
      }

      if (parentDistrict?.notes) {
        const note = parentDistrict.notes.find(note => note.startsWith(tier3Type + ':'));
        if (note) {
          setNoteGiven(note.split(':')[1]);
          setNote(note.split(':')[1]);
        }
      }
    }
  }, [open]);


  const handleAddChild = () => {
    setChildren((prev) => {
      const newArr: Child[] = [
        ...prev,
        {
          identifier: '',
          votingType: 'voting-district',
          existingDistrictId: undefined,
        },
      ];
      // The newly added row’s index = newArr.length - 1
      setFocusRowIndex(newArr.length - 1);
      return newArr;
    });
  };

  useEffect(() => {
    // If user selects "Has districts," ensure at least one row exists
    // and focus that row.
    if (districtGroupOption === 'has-districts' && children.length === 0) {
      setChildren([{ identifier: '', votingType: 'voting-district', existingDistrictId: undefined }]);
      setFocusRowIndex(0);
    }
  }, [districtGroupOption]);

  const canDeleteChild = (child: Child) => {
    if (!child.existingDistrictId) {
      // New unsaved row can always be removed.
      return true;
    }

    const existingDistrict = existingSubdistricts.find(d => d._id === child.existingDistrictId);

    const createdOver3DaysAgo = 
      !existingDistrict?.createdAt || 
      (moment(existingDistrict.createdAt).isBefore(moment().subtract(3, 'days')));
    return isPermitted(
      user,
      researcherPermissionsIncludingAndAfter(createdOver3DaysAgo ? 'publisher' : 'researcher')
    );
  };

  const toggleGeodistrictRemoval = (districtId: string) => {
    setPendingGeodistrictRemovals(current => {
      const next = new Set(current);
      if (next.has(districtId)) {
        next.delete(districtId);
      } else {
        next.add(districtId);
      }
      return next;
    });
  };

  // Whenever children changes or focusRowIndex changes, focus the appropriate row.
  useEffect(() => {
    if (
      focusRowIndex !== null &&
      focusRowIndex >= 0 &&
      focusRowIndex < children.length
    ) {
      textFieldRefs.current[focusRowIndex]?.focus();
    }
  }, [children, focusRowIndex]);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    rowIndex: number,
  ) => {
    // If user presses Enter or Tab in this field...
    if (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();  // Prevent the default tab/enter action
      // Add a new row and focus it
      handleAddChild();
    }
  };

  const saveInformation = async () => {
    if (!parentDistrict || !stateConfiguration || !tier3Type) return;
    if (saving) return;
    setSaving(true);
    setError(null);

    // Perform the operations based on operationsToPerform
    const {
      districts,
      childrenStructureValue,
    } = operationsToPerform;

    const sourcesAll: DistrictSource[] = [
      ...sources.filter(Boolean).map(source => ({ url: source.trim(), type: tier3Type })),
      ...parentDistrict.sources || [],
    ];
    // dedup the sources, taking preference for the most recent ones
    const sourcesToSave: DistrictSource[] = sourcesAll.filter((source, index, arr) => {
      return arr.findIndex(s => s.url === source.url && s.type === source.type) === index;
    })
    const noteModified = note.trim()?.length > 0 ? note.trim() : undefined;
    const notesToSave: string[] = (parentDistrict.notes || []).map(existingNote => {
      if(existingNote.startsWith(tier3Type + ':')) {
        return noteModified ? `${tier3Type}:${noteModified}` : undefined;
      } else {
        return existingNote;
      }
    }).filter((note): note is string => note !== undefined)

    if(noteModified && !notesToSave.find(note => note.startsWith(tier3Type + ':'))) {
      notesToSave.push(`${tier3Type}:${noteModified}`);
    }

    if (childrenStructureValue !== -1) {
      try {
        await Promise.all(
          geodistrictIdsToDelete.map(districtId => feathers.getService('geo-districts').remove(districtId))
        );
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError('An unknown error occurred while deleting geodistricts');
        }
        setSaving(false);
        return;
      }

      // first, perform the delete operations
      const deleteOperations = districts.filter((d): d is DeleteOperation => d.operationType === 'delete');
      const deletePromises = deleteOperations.map(({ id }) => feathers.getService('districts').remove(id));

      try {
        await Promise.all(deletePromises);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError('An unknown error occurred');
        }
        setSaving(false);
        return;
      }


      // next, patch the parent district childrenStructure
      const filteredExistingChildrenStructure = Object.fromEntries(
        Object.entries(parentDistrict.childrenStructure || {}).filter(([type, value]) => (
          value !== -1 || possibleChildTypesForParent.includes(type)
        )),
      );
      const newChildrenStructure = {
        ...filteredExistingChildrenStructure,
        [tier3Type]: childrenStructureValue,
        validatedAt: allDistrictsFound ? new Date().toISOString() : null,
      };

			let updatedParentDistrict;
      try {
        updatedParentDistrict = await feathers.getService('districts').patch(parentDistrict._id, {
          childrenStructure: newChildrenStructure,
          notes: notesToSave,
          sources: sourcesToSave
        });
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError('An unknown error occurred');
        }
        setSaving(false);
        return;
      }

      // Then perform creates and patches independently so one failed row does not
      // prevent the remaining manually mapped rows from being saved.
      try {
        const upsertOperations = districts.filter(
          (district): district is CreateOperation | PatchOperation => district.operationType !== 'delete',
        );
        const settledDistricts = await Promise.allSettled(upsertOperations.map((d) => {
          if (d.operationType === 'create') {
            return feathers.getService('districts').create(d.value);
          }
          return feathers.getService('districts').patch(d.id, d.value);
        }));
        const failures = settledDistricts.flatMap((result, index) => {
          if (result.status === 'fulfilled') return [];
          const operation = upsertOperations[index];
          const identifier = operation.value[identifierType];
          const message = result.reason instanceof Error ? result.reason.message : String(result.reason || 'Unknown error');
          return [`Row ${operation.rowIndex + 1} (${identifier ?? 'unknown'}): ${message}`];
        });

        const authoritativeDistricts: District[] = [];
        let totalDistricts = Number.POSITIVE_INFINITY;
        while (authoritativeDistricts.length < totalDistricts) {
          const authoritativeResult = await feathers.getService('districts').find({
            query: {
              parentId: parentDistrict._id,
              type: tier3Type,
              $limit: 250,
              $skip: authoritativeDistricts.length,
            },
          });
          authoritativeDistricts.push(...authoritativeResult.data);
          totalDistricts = authoritativeResult.total || authoritativeResult.data.length;
          if (!authoritativeResult.data.length) break;
        }

        if (authoritativeDistricts.length !== childrenStructureValue) {
          updatedParentDistrict = await feathers.getService('districts').patch(parentDistrict._id, {
            childrenStructure: {
              ...newChildrenStructure,
              [tier3Type]: authoritativeDistricts.length,
            },
          });
        }

        if (failures.length) {
          setError(`Some rows could not be saved:\n${failures.join('\n')}`);
          setChildren(authoritativeDistricts.map(district => ({
            identifier: identifierValueForDistrict(district),
            votingType: district.votingType,
            existingDistrictId: district._id,
          })));
          setSaving(false);
        }

        onFinish(updatedParentDistrict, authoritativeDistricts, failures.length > 0);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError('An unknown error occurred');
        }

        setSaving(false);
      }
      return;
    }
  };

  let contentInner = null;
  if (parentDistrict && stateConfiguration && tier3Type) {
    contentInner = (
      <ModalContent>
        <ModalBody>
        <Typography variant="h2">
          {existingSubdistricts.length > 0 ? 'Edit' : 'Add'} {humanizedTier3Type}
        </Typography>
        {
          error &&
          <Typography variant="body2" style={{ color: 'red', whiteSpace: 'pre-line' }}>{error}</Typography>
        }
        <div style={{ marginTop: '12px', paddingLeft: '16px'  }}>
          <Typography variant='body1' style={{ fontWeight: 'bold', marginLeft: '-16px' }}>Sources</Typography>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
            {(sources.slice(0, -1)).map((source, index) => (
              <Chip
                variant="outlined"
                key={index}
                label={
                  <a href={source} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                  {source}
                  </a>
                }
                onDelete={() => {
                  setSources(sources.filter((_, i) => i !== index));
                }}
              />
            ))}
             <TextField
                InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => {
                      const trimmedValue = sources.length ? sources[sources.length - 1].trim() : '';
                      if (trimmedValue.length > 0) {
                        setSources(s => [...s, '']);
                      }
                    }}
                  >
                    <KeyboardReturn />
                  </IconButton>
                ),
                }}
              label="Add source"
              variant="outlined"
              size="small"
              value={sources[sources.length - 1] || ''}
              onChange={(event) => setSources([...sources.slice(0, sources.length - 1), event.target.value])}
              onKeyDown={(event) => {
                const trimmedValue = sources.length ? sources[sources.length - 1].trim() : '';
                if (event.key === 'Enter' && trimmedValue.length > 0) {
                  setSources(s => [...s, '']);
                }
              }}
            />
          </div> 
          <div style={{ marginTop: '12px' }}>
            <TextField
              label="Note"
              variant="outlined"
              size="small"
              fullWidth
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </div>
        <div style={{ marginTop: '24px', paddingLeft: '16px' }}>
          <Typography variant='body1' style={{ fontWeight: 'bold', marginLeft: '-16px' }}>Subdistricts</Typography>
          <RadioGroup
            value={districtGroupOption}
            onChange={(event) => setDistrictGroupOption(event.target.value as 'has-districts' | 'no-districts')}
          >
            <FormControlLabel value="no-districts" control={<Radio />} label="Does not have districts" />
            <FormControlLabel value="has-districts" control={<Radio />} label="Has districts" />
          </RadioGroup>
        </div>
        {
          districtGroupOption === 'has-districts' &&
          <>
            <div style={{ marginLeft: '16px', marginTop: '12px' }}>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>Identifier type</Typography>
              <RadioGroup
                row
                value={identifierType}
                onChange={(event) => setIdentifierType(event.target.value as DistrictIdentifierType)}
              >
                <FormControlLabel value="name" control={<Radio size="small" />} label="Name" />
                <FormControlLabel value="number" control={<Radio size="small" />} label="Number" />
                <FormControlLabel value="letter" control={<Radio size="small" />} label="Letter" />
              </RadioGroup>
              {conversionSummary && (
                <Typography variant="body2" color="warning.main">{conversionSummary}</Typography>
              )}
            </div>
            <TableContainer component={Paper} style={{ marginLeft: '16px', marginTop: '12px', border: 'solid 1px #dddddd', maxWidth: 660 }}>
              <Table size="small" aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>Identifier</TableCell>
                    <TableCell>Preview</TableCell>
                    <TableCell>At-large?</TableCell>
                    <TableCell></TableCell>
                    <TableCell>Geodistrict</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    children.map((child, idx) => {
                      const existingDistrict = child.existingDistrictId
                        ? existingSubdistricts.find(district => district._id === child.existingDistrictId)
                        : undefined;
                      const hasGeodistrict = Boolean(existingDistrict?.geoDistrict?.boundary);
                      const hasPartialBoundary = existingDistrict?.geoDistrict?.partialBoundary === true;
                      const geodistrictRemovalPending = Boolean(
                        child.existingDistrictId && pendingGeodistrictRemovals.has(child.existingDistrictId)
                      );

                      return (
                      <TableRow key={child.existingDistrictId || idx} style={{ backgroundColor: idx % 2 === 0 ? '#f8f8f8' : '#FFFFFF' }}>
                        <TableCell>
                          <TextField
                            size="small"
                            variant="standard"
                            style={{ backgroundColor: 'transparent' }}
                            value={child.identifier}
                            onChange={(event) => {
                              const newChildren = [...children];
                              newChildren[idx].identifier = identifierType === 'letter'
                                ? event.target.value.toUpperCase()
                                : event.target.value;
                              setChildren(newChildren);
                            }}
                            error={Boolean(identifierValidation.rowErrors[idx])}
                            helperText={identifierValidation.rowErrors[idx] || undefined}
                            inputRef={(el) => {
                              // Store the input reference in an array
                              textFieldRefs.current[idx] = el as HTMLInputElement;
                            }}
                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, idx)}
                          />
                        </TableCell>
                        <TableCell>
                          {
                            orderedNamingPatterns.length === 0 &&
                            <Skeleton variant="text" width={100} />
                          }
                          {
                            Boolean(child.identifier) &&
                            <Typography variant='body1'>
                              {prettyDistrictNameFromPattern({
                                ...identifierPayload(identifierType, child.identifier),
                                type: tier3Type,
                                parent: parentDistrict?.name,
                              }, orderedNamingPatterns)?.longName || ''}
                            </Typography>
                          }
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Checkbox
                              checked={child.votingType === 'representative-district'}
                              size="small"
                              onChange={(event) => {
                                const newChildren = [...children];
                                newChildren[idx].votingType = event.target.checked ? 'representative-district' : 'voting-district';
                                setChildren(newChildren);
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={canDeleteChild(child) ? 'Remove district' : 'Requires publisher+ to delete this child district'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!canDeleteChild(child)}
                                onClick={() => {
                                  const newChildren = [...children];
                                  newChildren.splice(idx, 1);
                                  setChildren(newChildren);
                                }}
                              >
                                <Cancel style={{ fontSize: '18px' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {hasPartialBoundary && (
                              <Chip size="small" color="warning" label="Partial" />
                            )}
                            {hasGeodistrict && existingDistrict ? (
                              <Tooltip title={geodistrictRemovalPending ? 'Keep geodistrict' : 'Delete geodistrict'}>
                                <IconButton
                                  size="small"
                                  aria-label={geodistrictRemovalPending ? 'Keep geodistrict' : 'Delete geodistrict'}
                                  onClick={() => toggleGeodistrictRemoval(existingDistrict._id)}
                                >
                                  <GeodistrictDeleteIcon $pendingRemoval={geodistrictRemovalPending}>
                                    <MapOutlined />
                                    <Cancel />
                                  </GeodistrictDeleteIcon>
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <div/>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })
                  }
                  <TableRow>
                    <TableCell>
                      <Button size="small" onClick={handleAddChild}>Add</Button>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        }
        </ModalBody>
        {
          operationPending &&
          <ModalFooter>
            {(operationSummary || operationBlockedReason) && (
              <Typography variant="body2">{operationSummary || operationBlockedReason}</Typography>
            )}
            <ModalFooterActions>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allDistrictsFound}
                    disabled={saving}
                    onChange={(event) => setAllDistrictsFound(event.target.checked)}
                  />
                }
                label="All districts found"
              />
              <Button
                variant="contained"
                disabled={saving || !operationReady}
                onClick={saveInformation}
              >
                {saving ? 'Saving...' : 'Confirm'}
              </Button>
            </ModalFooterActions>
          </ModalFooter>
        }
      </ModalContent>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalInner onClose={onClose} style={{ maxWidth: '700px' }}>
        {contentInner}
      </ModalInner>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 12px;
  padding: 16px 0;
  background: #ffffff;
  border-top: solid 1px #dddddd;
`;

const ModalFooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const GeodistrictDeleteIcon = styled.span<{ $pendingRemoval: boolean }>`
  position: relative;
  display: inline-flex;
  width: 28px;
  height: 28px;
  opacity: ${props => props.$pendingRemoval ? 0.45 : 1};

  > svg:first-child {
    width: 28px;
    height: 28px;
  }

  > svg:last-child {
    position: absolute;
    top: -4px;
    right: -6px;
    width: 15px;
    height: 15px;
    background: #ffffff;
    border-radius: 50%;
  }
`;

export { Tier3AddDistrictModal };